// Library Methods
import { useEffect, useMemo } from "react";

// Components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

// Utils
import { getDecimalNum } from "src/utils/number/numberHelper";

// Types
import { ExamSectionReportType } from "src/services/exams/types";

type RAPDPupilsType = {
  chartData: ExamSectionReportType["state"];
};

const RAPDPupils = ({ chartData }: RAPDPupilsType) => {
  // get the latest data added to the pupil events array
  const currentPupilData = useMemo(() => {
    const pupilDataLength = chartData?.pupilEventData?.length ?? 0;
    return chartData?.pupilEventData?.[pupilDataLength - 1];
  }, [chartData?.pupilEventData]);

  // every time new pupil data is added, update the displayed pupil sizes.
  // Scaled to a smaller multiplier than a full report page (x6 instead of
  // x10) since this renders inside the height-constrained live update panel:
  // min pupil size is 18px (3mm) and max pupil size is 54px (9mm)
  useEffect(() => {
    if (
      !currentPupilData?.pupilDiameterLeft ||
      !currentPupilData?.pupilDiameterRight
    )
      return;

    const leftSize =
      Number(getDecimalNum(currentPupilData?.pupilDiameterLeft, 1)) * 6;
    const rightSize =
      Number(getDecimalNum(currentPupilData?.pupilDiameterRight, 1)) * 6;

    const leftPupil = document.getElementById("left-pupil");
    if (leftPupil) {
      leftPupil.style.width = `${leftSize}px`;
      leftPupil.style.height = `${leftSize}px`;
    }

    const rightPupil = document.getElementById("right-pupil");
    if (rightPupil) {
      rightPupil.style.width = `${rightSize}px`;
      rightPupil.style.height = `${rightSize}px`;
    }
  }, [currentPupilData]);

  return (
    <Grid display="flex" justifyContent="center" gap="50px">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          alignItems: "center",
        }}
      >
        <Box
          id="left-iris"
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "60px",
            height: "60px",
            borderRadius: "50%",
            backgroundColor: "#FF000070",
            transition: "width 0.5s ease, height 0.5s ease",
          }}
        >
          <Box
            id="left-pupil"
            sx={{
              width: "30px",
              height: "30px",
              borderRadius: "50%",
              backgroundColor: "black",
              transition: "width 0.5s ease, height 0.5s ease",
            }}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {currentPupilData?.brightnessDBLeft && (
            <Typography fontSize="14px">
              {currentPupilData?.brightnessDBLeft} dB
            </Typography>
          )}
          {currentPupilData?.pupilDiameterLeft && (
            <Typography fontSize="14px">
              {getDecimalNum(currentPupilData?.pupilDiameterLeft, 1)} mm
            </Typography>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          alignItems: "center",
        }}
      >
        <Box
          id="right-iris"
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "60px",
            height: "60px",
            borderRadius: "50%",
            backgroundColor: "#0000FF70",
            transition: "width 0.5s ease, height 0.5s ease",
          }}
        >
          <Box
            id="right-pupil"
            sx={{
              width: "30px",
              height: "30px",
              borderRadius: "50%",
              backgroundColor: "black",
              transition: "width 0.5s ease, height 0.5s ease",
            }}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {currentPupilData?.brightnessDBRight && (
            <Typography fontSize="14px">
              {currentPupilData?.brightnessDBRight} dB
            </Typography>
          )}
          {currentPupilData?.pupilDiameterRight && (
            <Typography fontSize="14px">
              {getDecimalNum(currentPupilData?.pupilDiameterRight, 1)} mm
            </Typography>
          )}
        </Box>
      </Box>
    </Grid>
  );
};

export default RAPDPupils;
